import { createCsrfFeedbackDialog } from './feedback-dialog-module.js';

/**
 * Lightweight, dependency-free autocomplete used across CT site.
 * Pure ES module export, no global symbols.
 *
 * Wires the input + suggestion list as a WAI-ARIA 1.2 combobox-with-listbox pattern.
 *
 * @param {string|HTMLInputElement} selector CSS selector for one or more input elements OR a single input element.
 * @param {string|function(): string} serviceUrl Endpoint (GET) receiving `?query=<term>`. Pass a function instead of a string when the URL needs to read live state per fetch (e.g. a country query param that depends on the current form value).
 * @param {function(object, HTMLInputElement): void} onSelect Callback to run when a suggestion is selected.
 * @param {function(object, string, object=): string} formatResult Function to format a suggestion. Must return an HTML string. The optional third argument is the full parsed response, useful when the server attaches per-request metadata (e.g. a parsed-query breakdown for highlighting).
 * @param {object} [options] Optional settings.
 * @param {number} [options.minChars=3] Minimum characters to trigger autocomplete.
 * @param {number} [options.debounce=250] Debounce time in milliseconds before fetching.
 * @param {string} [options.containerClass] Optional CSS class for the suggestion container.
 * @param {string} [options.ariaLabel] Optional accessible name announced for the suggestion listbox.
 * @param {string} [options.noResultsMessage=''] Visible affordance shown when a query returns zero suggestions. Default '' closes the dropdown silently.
 * @param {function(number): string} [options.formatResultCount] Builds the aria-live count message announced when suggestions arrive. Receives the suggestion count.
 */
let autoCompleteInstanceCount = 0;

// Short delay before re-announcing the result count: SR engines coalesce textContent that
// hasn't changed, so we clear the live region and let one tick pass before re-writing.
const SR_ANNOUNCE_DELAY_MS = 50;

export function createAutoComplete(selector, serviceUrl, onSelect, formatResult, options) {
  options = options || {};
  const minChars = typeof options.minChars === 'number' ? options.minChars : 3;
  const debounceMilliseconds = typeof options.debounce === 'number' ? options.debounce : 250;
  const resultsClass = 'autocomplete-suggestions';
  const itemClass = 'autocomplete-suggestion';
  const emptyClass = 'autocomplete-empty';
  const position = options.position || 'absolute';
  const parent = options.parent || document.body;
  const noResultsMessage = typeof options.noResultsMessage === 'string' ? options.noResultsMessage : '';
  const formatResultCount =
    typeof options.formatResultCount === 'function'
      ? options.formatResultCount
      : (count) =>
          count === 0
            ? noResultsMessage || 'Keine Vorschläge'
            : count === 1
              ? '1 Vorschlag verfügbar'
              : `${count} Vorschläge verfügbar`;

  const inputs = typeof selector === 'string' ? document.querySelectorAll(selector) : [selector];
  inputs.forEach(function (input) {
    if (!input) {
      return;
    }
    let debounceTimer = null;
    let abortController = null;
    let suggestions = [];
    let highlightedIndex = -1;
    const instanceId = ++autoCompleteInstanceCount;
    const listboxId = 'autocomplete-listbox-' + instanceId;
    const optionId = (idx) => 'autocomplete-listbox-' + instanceId + '-option-' + idx;

    const container = document.createElement('div');
    container.className = resultsClass + (options.containerClass ? ' ' + options.containerClass : '');
    container.id = listboxId;
    container.setAttribute('role', 'listbox');
    if (options.ariaLabel) {
      container.setAttribute('aria-label', options.ariaLabel);
    }
    container.style.position = position;
    container.style.display = 'none';
    container.style.zIndex = '9999';
    parent.appendChild(container);

    // Off-screen status line announced by screen readers when a fetch settles. Distinct
    // from the listbox container so VoiceOver/NVDA hear the count even before users move
    // focus into the listbox.
    const liveRegion = document.createElement('span');
    liveRegion.className = 'sr_only';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    parent.appendChild(liveRegion);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', listboxId);
    input.setAttribute('aria-expanded', 'false');

    function updatePosition() {
      const rect = input.getBoundingClientRect();
      container.style.width = rect.width + 'px';
      if (position === 'fixed') {
        container.style.left = rect.left + 'px';
        container.style.top = rect.bottom + 'px';
      } else {
        container.style.left = window.scrollX + rect.left + 'px';
        container.style.top = window.scrollY + rect.bottom + 'px';
      }
    }

    function close() {
      container.style.display = 'none';
      container.innerHTML = '';
      highlightedIndex = -1;
      suggestions = [];
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      // Defensively clear any reference to a highlighted suggestion on the input.
      try {
        input._acHighlightedSuggestion = null;
      } catch (e) {
        /* ignore */
      }
    }

    function open() {
      updatePosition();
      container.style.display = 'block';
      input.setAttribute('aria-expanded', 'true');
    }

    function highlight(index) {
      const items = container.querySelectorAll('.' + itemClass);
      items.forEach(function (el) {
        el.classList.remove('autocomplete-selected');
        el.setAttribute('aria-selected', 'false');
      });
      if (index >= 0 && index < items.length) {
        items[index].classList.add('autocomplete-selected');
        items[index].setAttribute('aria-selected', 'true');
        items[index].scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', items[index].id);
        highlightedIndex = index;
        // expose currently highlighted suggestion on the input for external handlers
        try {
          input._acHighlightedSuggestion = suggestions[index] || null;
        } catch (e) {
          /* ignore */
        }
      } else {
        highlightedIndex = -1;
        input.removeAttribute('aria-activedescendant');
        // Defensively clear any reference to a highlighted suggestion on the input.
        try {
          input._acHighlightedSuggestion = null;
        } catch (e) {
          /* ignore */
        }
      }
    }

    function selectIndex(index) {
      if (index >= 0 && index < suggestions.length) {
        const suggestion = suggestions[index];
        close();
        if (typeof onSelect === 'function') {
          try {
            onSelect(suggestion, input);
          } catch (e) {
            console.error(e);
          }
        }
      }
    }

    function render(list, query, response) {
      container.innerHTML = '';
      list.forEach(function (sugg, idx) {
        const item = document.createElement('div');
        item.className = itemClass;
        item.id = optionId(idx);
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.setAttribute('data-index', String(idx));
        try {
          item.innerHTML = typeof formatResult === 'function' ? formatResult(sugg, query, response) : sugg.value || '';
        } catch (e) {
          item.textContent = sugg.value || '';
        }
        item.addEventListener('mouseenter', function () {
          highlight(idx);
        });
        item.addEventListener('mouseleave', function () {
          highlight(-1);
        });
        // mousedown to run before input blur
        item.addEventListener('mousedown', function (ev) {
          ev.preventDefault();
          selectIndex(idx);
        });
        container.appendChild(item);
      });
      announceResultCount(list.length);
      if (list.length > 0) {
        open();
      } else if (noResultsMessage !== '') {
        renderEmptyState();
      } else {
        close();
      }
    }

    function renderEmptyState() {
      const empty = document.createElement('div');
      empty.className = emptyClass;
      empty.textContent = noResultsMessage;
      container.appendChild(empty);
      open();
    }

    function announceResultCount(count) {
      // Force a re-announce on identical counts (two empty searches in a row) by clearing
      // first, then setting the new value after one tick.
      liveRegion.textContent = '';
      window.setTimeout(function () {
        liveRegion.textContent = formatResultCount(count);
      }, SR_ANNOUNCE_DELAY_MS);
    }

    let lastRequestedQuery = '';

    function cancelPendingFetch() {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      if (abortController) {
        abortController.abort();
      }
    }

    function fetchSuggestions(query) {
      // Track the query we requested; used to ignore stale responses
      lastRequestedQuery = query;
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();
      const resolvedServiceUrl = typeof serviceUrl === 'function' ? serviceUrl() : serviceUrl;
      const url =
        resolvedServiceUrl +
        (resolvedServiceUrl.indexOf('?') === -1 ? '?' : '&') +
        'query=' +
        encodeURIComponent(query);
      fetch(url, {
        method: 'GET',
        headers: { 'X-CSRF-Token': typeof fetchCsrfToken === 'function' ? fetchCsrfToken() : '' },
        signal: abortController.signal,
      })
        .then(function (resp) {
          return resp.json();
        })
        .then(function (res) {
          // A token error concerns the session, not this query, so report it before the checks below.
          if (res && res.error_csrf) {
            createCsrfFeedbackDialog(res);
            return;
          }
          // Drop a response the user has moved on from: a late one would reopen the dropdown
          // over the field they moved to.
          const currentVal = input.value.trim();
          if (currentVal.length < minChars || currentVal !== lastRequestedQuery || document.activeElement !== input) {
            close();
            return;
          }
          const list = (res && (res.suggestions || res.results || res.data)) || [];
          suggestions = list;
          render(list, currentVal, res);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') {
            return;
          }
          console.error(err);
        });
    }

    function onInput() {
      const q = input.value.trim();
      if (q.length < minChars) {
        cancelPendingFetch();
        close();
        return;
      }
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(function () {
        fetchSuggestions(q);
      }, debounceMilliseconds);
    }

    function onKeyDown(e) {
      const isVisible = container.style.display !== 'none';
      if (e.key === 'Escape') {
        // Cancel the pending fetch too, or dismissed suggestions reappear once it resolves.
        cancelPendingFetch();
        if (isVisible) {
          close();
        }
        return;
      }
      const hasNavigableSuggestions = isVisible && suggestions.length > 0;
      if (!hasNavigableSuggestions) {
        return;
      }
      const max = suggestions.length;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlight((highlightedIndex + 1) % max);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlight((highlightedIndex - 1 + max) % max);
      } else if (e.key === 'Enter') {
        if (highlightedIndex >= 0) {
          // Select highlighted suggestion and prevent form submission
          e.preventDefault();
          if (typeof e.stopPropagation === 'function') {
            e.stopPropagation();
          }
          selectIndex(highlightedIndex);
        } else {
          // No highlighted suggestion: close dropdown and allow form submit
          close();
          // Intentionally do not preventDefault; let form handlers/default submit run
        }
      } else if (e.key === 'Tab') {
        // Commit a highlighted suggestion, then let Tab navigate to the next field naturally.
        if (highlightedIndex >= 0) {
          selectIndex(highlightedIndex);
        } else {
          close();
        }
      }
    }

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeyDown);

    // Selecting with the mouse does not blur: the suggestion's mousedown handler calls preventDefault().
    input.addEventListener('blur', cancelPendingFetch);

    // If minChars is 0, request on focus as well
    input.addEventListener('focus', function () {
      if (minChars === 0) {
        const q = input.value.trim();
        fetchSuggestions(q);
      }
    });

    document.addEventListener('click', function (event) {
      if (event.target !== input && !container.contains(event.target)) {
        close();
      }
    });

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
  });
}

// Expose the function to the global scope for legacy scripts
// TODO remove this after migrating legacy scripts to modules
window.createAutoComplete = createAutoComplete;
